import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://ezer-convivencia-backend.azurewebsites.net/api/v1";

// Create Axios Instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // For cookie support in refresh tokens
});

// Request interceptor to inject JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("ezer_access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle session expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If token expired, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );
        const { access_token } = refreshResponse.data;
        localStorage.setItem("ezer_access_token", access_token);
        
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear token and redirect to login
        localStorage.removeItem("ezer_access_token");
        localStorage.removeItem("ezer_user");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// =========================================================================
// API Services Wrapper
// =========================================================================

// Auth Services
export const authService = {
  login: async (credentials: any) => {
    const res = await api.post("/auth/login", credentials);
    const { access_token, user } = res.data;
    localStorage.setItem("ezer_access_token", access_token);
    localStorage.setItem("ezer_user", JSON.stringify(user));
    return user;
  },
  logout: async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("ezer_access_token");
      localStorage.removeItem("ezer_user");
      window.location.href = "/login";
    }
  },
  getMe: async () => {
    const res = await api.get("/auth/me");
    return res.data;
  }
};

// Tenant Services (Superadmin)
export const tenantService = {
  create: async (tenantData: any) => {
    const res = await api.post("/tenants", tenantData);
    return res.data;
  },
  list: async () => {
    const res = await api.get("/tenants");
    return res.data;
  },
  toggleStatus: async (tenantId: string, activo: boolean) => {
    const res = await api.patch(`/tenants/${tenantId}/status`, { activo });
    return res.data;
  }
};

// Cases Services
export const caseService = {
  list: async (filters: any = {}) => {
    const res = await api.get("/casos", { params: filters });
    return res.data;
  },
  create: async (caseData: any) => {
    const res = await api.post("/casos", caseData);
    return res.data;
  },
  get: async (caseId: string) => {
    const res = await api.get(`/casos/${caseId}`);
    return res.data;
  },
  update: async (caseId: string, caseData: any) => {
    const res = await api.put(`/casos/${caseId}`, caseData);
    return res.data;
  },
  transitionStatus: async (caseId: string, estado: string) => {
    const res = await api.patch(`/casos/${caseId}/status`, { estado });
    return res.data;
  },
  getTimeline: async (caseId: string) => {
    const res = await api.get(`/casos/${caseId}/timeline`);
    return res.data;
  }
};

// Interventions Services
export const interventionService = {
  list: async (filters: any = {}) => {
    const res = await api.get("/intervenciones", { params: filters });
    return res.data;
  },
  create: async (intData: any) => {
    const res = await api.post("/intervenciones", intData);
    return res.data;
  },
  get: async (intId: string) => {
    const res = await api.get(`/intervenciones/${intId}`);
    return res.data;
  },
  update: async (intId: string, intData: any) => {
    const res = await api.put(`/intervenciones/${intId}`, intData);
    return res.data;
  },
  delete: async (intId: string) => {
    const res = await api.delete(`/intervenciones/${intId}`);
    return res.data;
  }
};

// Documents & Evidences Services
export const documentService = {
  uploadEvidencia: async (formData: FormData) => {
    const res = await api.post("/documentos/upload/evidencia", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return res.data;
  },
  uploadInstitucional: async (formData: FormData) => {
    const res = await api.post("/documentos/upload/institucional", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return res.data;
  },
  listInstitucionales: async () => {
    const res = await api.get("/documentos/institucionales");
    return res.data;
  },
  listEvidencias: async () => {
    const res = await api.get("/documentos/evidencias");
    return res.data;
  },
  getDownloadUrl: async (container: string, blobPath: string) => {
    const res = await api.get(`/documentos/download/${container}/${blobPath}`);
    return res.data.download_url;
  }
};

// Teacher Inputs Services
export const inputService = {
  createSolicitud: async (data: any) => {
    const res = await api.post("/insumos", data);
    return res.data;
  },
  listPendientes: async () => {
    const res = await api.get("/insumos/pendientes");
    return res.data;
  },
  listSolicitados: async () => {
    const res = await api.get("/insumos/solicitados");
    return res.data;
  },
  submitRespuesta: async (insumoId: string, respuesta: string) => {
    const res = await api.put(`/insumos/${insumoId}/respuesta`, { respuesta });
    return res.data;
  },
  updateStatus: async (insumoId: string, estado: string) => {
    const res = await api.patch(`/insumos/${insumoId}/status`, { estado });
    return res.data;
  },
  update: async (insumoId: string, data: any) => {
    const res = await api.put(`/insumos/${insumoId}`, data);
    return res.data;
  }
};

// Court Orders / Judicial Services
export const judicialService = {
  create: async (data: any) => {
    const res = await api.post("/judicial", data);
    return res.data;
  },
  list: async () => {
    const res = await api.get("/judicial");
    return res.data;
  },
  get: async (reqId: string) => {
    const res = await api.get(`/judicial/${reqId}`);
    return res.data;
  },
  update: async (reqId: string, data: any) => {
    const res = await api.put(`/judicial/${reqId}`, data);
    return res.data;
  },
  updateStatus: async (reqId: string, estado: string) => {
    const res = await api.patch(`/judicial/${reqId}/status`, { estado });
    return res.data;
  }
};

// AI Services
export const aiService = {
  evaluarLenguaje: async (texto: string) => {
    const res = await api.post("/ia/evaluar-lenguaje", { texto });
    return res.data;
  },
  consultaNormativa: async (pregunta: string) => {
    const res = await api.post("/ia/consulta-normativa", { pregunta });
    return res.data; // returns { respuesta, consulta_id }
  },
  resumenCaso: async (casoId: string) => {
    const res = await api.post(`/ia/resumen-caso/${casoId}`);
    return res.data.resumen;
  },
  borradorInforme: async (casoId: string) => {
    const res = await api.post(`/ia/borrador-informe/${casoId}`);
    return res.data.informe_borrador;
  },
  extractJudicial: async (textoOficio: string) => {
    const res = await api.post("/ia/extract-judicial", { texto_oficio: textoOficio });
    return res.data;
  },
  listConsultas: async (filters: any = {}) => {
    const res = await api.get("/ia/consultas", { params: filters });
    return res.data; // returns { consultas, stats, gaps }
  },
  submitFeedback: async (consultaId: string, util: boolean) => {
    const res = await api.post(`/ia/consulta/${consultaId}/feedback`, { util });
    return res.data;
  },
  resolverBrecha: async (consultaId: string, resolucion: string) => {
    const res = await api.post(`/ia/consulta/${consultaId}/resolver`, { resolucion });
    return res.data;
  }
};

// Estudiantes Services
export const estudianteService = {
  list: async (filters: any = {}) => {
    const res = await api.get("/estudiantes", { params: filters });
    return res.data;
  },
  get: async (id: string) => {
    const res = await api.get(`/estudiantes/${id}`);
    return res.data;
  },
  create: async (data: any) => {
    const res = await api.post("/estudiantes", data);
    return res.data;
  },
  update: async (id: string, data: any) => {
    const res = await api.put(`/estudiantes/${id}`, data);
    return res.data;
  },
  delete: async (id: string) => {
    const res = await api.delete(`/estudiantes/${id}`);
    return res.data;
  },
  reactivate: async (id: string) => {
    const res = await api.post(`/estudiantes/${id}/reactivar`);
    return res.data;
  }
};

// Dashboard Services
export const dashboardService = {
  getKpis: async () => {
    const res = await api.get("/dashboard/kpis");
    return res.data;
  }
};

// Notification Services
export const notificationService = {
  list: async () => {
    const res = await api.get("/notificaciones");
    return res.data;
  },
  countUnread: async () => {
    const res = await api.get("/notificaciones/no-leidas/count");
    return res.data;
  },
  markAsRead: async (notificationId: string) => {
    const res = await api.patch(`/notificaciones/${notificationId}/leer`);
    return res.data;
  },
  markAllAsRead: async () => {
    const res = await api.patch("/notificaciones/leer-todas");
    return res.data;
  }
};

// Usuarios Services
export const userService = {
  list: async (filters: any = {}) => {
    const res = await api.get("/usuarios", { params: filters });
    return res.data;
  },
  create: async (userData: any) => {
    const res = await api.post("/usuarios", userData);
    return res.data;
  },
  update: async (userId: string, userData: any) => {
    const res = await api.patch(`/usuarios/${userId}`, userData);
    return res.data;
  },
  toggleStatus: async (userId: string) => {
    const res = await api.patch(`/usuarios/${userId}/status`);
    return res.data;
  }
};
